import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ColActionId, DocumentInfo, DocumentPermission, IconType, IoniconsOutline } from '@pt/pt-models';
import { Store } from '@ngrx/store';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import { DocumentPermissionService } from '@pt/pt-core';

@Component({
  selector: 'pt-editor-sidebar-menu-btn',
  template: `
    <div class='position-relative d-inline-block'
         ngbDropdown
         autoClose="'outside'"
         container="body"
         [ngClass]='{"cursor-not-allowed": disabled }'>
      <button class='btn btn-sm btn-link btn-menu'
              id="dropdownForm1" ngbDropdownToggle
              [disabled]='disabled'
              (click)='openMenu()'>
        <pt-icon [iconType]='IconType.IONIC_ICON'
                 [key]='IconMenu'
                 [size]='14'>
        </pt-icon>
      </button>

      <div ngbDropdownMenu aria-labelledby="dropdownForm1" class="sidebar-menu">
        <div class="spinner-container" *ngIf="loading">
          <div class="spinner-border" role="status"></div>
        </div>
        <div>
          <button
            *ngIf="showBtnDuplicateDocument"
            [disabled]='disabledDuplicateDocument'
            [ngbTooltip]="'BUTTON.DUPLICATE' | translate"
            placement='bottom'
            ngbDropdownItem
            type='button'
            class='btn-sm'
            (click)='duplicateDocument()'>
            <span>{{ 'BUTTON.DUPLICATE' | translate }}</span>
          </button>
          <button
            *ngIf="showBtnSnapshotDocument"
            [disabled]='disabledSnapshotDocument'
            [ngbTooltip]="'BUTTON.SNAPSHOT' | translate"
            placement='bottom'
            ngbDropdownItem
            type='button'
            class='btn-sm'
            (click)='snapshotDocument()'>
            <span>{{ 'BUTTON.SNAPSHOT' | translate }}</span>
          </button>
          <button
            *ngIf="showBtnValidateDocument"
            [disabled]='disabledValidateDocument'
            [ngbTooltip]="'BUTTON.VALIDATE' | translate"
            placement='bottom'
            ngbDropdownItem
            type='button'
            class='btn-sm'
            (click)='validateDocument()'>
            <span>{{ 'BUTTON.VALIDATE' | translate }}</span>
          </button>
          <hr class='mt-2 mb-2'>
          <button
            *ngIf="showBtnDeleteDocument"
            [disabled]='disabledDeleteDocument'
            ngbDropdownItem
            type='button'
            class='btn-sm'
            [ngbTooltip]="'LABELS.DELETE' | translate"
            placement='bottom'
            (click)='deleteDocument()'>
            <span>{{ 'LABELS.DELETE' | translate }}</span>
          </button>
        </div>

      </div>
    </div>
  `,
  styles: [`
    .sidebar-menu {
      position: relative;
      width: 130px;
      min-height: 130px;
    }

    .spinner-container {
      position: absolute;
      top: calc(50% - 10px);
      left: calc(50% - 10px);
    }

    .sidebar-menu .spinner-border {
      width: 20px;
      height: 20px;
    }

    .btn-menu {
      font-size: 11px;
      display: flex;
      height: 25px;
      min-width: max-content;
      align-items: center;
      border: solid 1px currentColor;
    }
  `]
})
export class EditorSidebarMenuBtnComponent {
  @Input() loading: boolean;
  @Input() disabled: boolean;
  @Input() documentInfo: DocumentInfo;
  @Input() availableOperations: AvailableOperations;
  @Output() documentActionEvent = new EventEmitter<ColActionId>();


  IconType = IconType;
  IconMenu = IoniconsOutline.menu;

  public disabledPublishDocument = true;
  public disabledReopenDocument = true;
  public disabledDeleteDocument = true;
  public disabledDownloadDocument = true;
  public disabledDuplicateDocument = true;
  public disabledValidateDocument = true;
  public disabledSnapshotDocument = true;

  public showBtnPublishDocument = true;
  public showBtnReopenDocument = true;
  public showBtnDeleteDocument = true;
  public showBtnDownloadDocument = true;
  public showBtnDuplicateDocument = true;
  public showBtnValidateDocument = true;
  public showBtnSnapshotDocument = true;

  constructor(private store: Store,
              private documentPermissionService: DocumentPermissionService) {
  }

  openMenu() {
    this.loading = true;

    this.disabledDeleteDocument = true;
    this.disabledPublishDocument = true;
    this.disabledReopenDocument = true;
    this.disabledDownloadDocument = true;
    this.disabledDuplicateDocument = true;
    this.disabledValidateDocument = true;

    this.showBtnDeleteDocument = true;
    this.showBtnPublishDocument = true;
    this.showBtnReopenDocument = true;
    this.showBtnDownloadDocument = true;
    this.showBtnDuplicateDocument = true;
    this.showBtnValidateDocument = true;
    this.showBtnSnapshotDocument = true;

    const permission: DocumentPermission = this.documentPermissionService.toDocPermission(this.documentInfo.docType, this.availableOperations);
    this.showBtnDeleteDocument = permission.deleteBtnVisible;
    this.showBtnPublishDocument = permission.publishBtnVisible;
    this.showBtnReopenDocument = permission.reopenBtnVisible;
    this.showBtnDownloadDocument = permission.downloadBtnVisible;
    this.showBtnDuplicateDocument = permission.duplicateBtnVisible;
    this.showBtnValidateDocument = permission.validateBtnVisible;
    this.showBtnSnapshotDocument = permission.snapshotBtnVisible;

    this.disabledDeleteDocument = !permission.deleteBtnEnabled;
    this.disabledPublishDocument = !permission.publishBtnEnabled;
    this.disabledReopenDocument = !permission.reopenBtnEnabled;
    this.disabledDownloadDocument = !permission.downloadBtnEnabled;
    this.disabledDuplicateDocument = !permission.duplicateBtnEnabled;
    this.disabledValidateDocument = !permission.validateBtnEnabled;
    this.disabledSnapshotDocument = !permission.snapshotBtnEnabled;

    this.loading = false;
  }

  deleteDocument() {
    this.documentActionEvent.emit(ColActionId.delete);
  }

  downloadDocument() {
    this.documentActionEvent.emit(ColActionId.download);
  }

  duplicateDocument() {
    this.documentActionEvent.emit(ColActionId.duplicate);
  }

  publishDocument() {
    this.documentActionEvent.emit(ColActionId.publish);
  }

  reopenDocument() {
    this.documentActionEvent.emit(ColActionId.reopen);
  }

  validateDocument() {
    this.documentActionEvent.emit(ColActionId.validate);
  }

  snapshotDocument() {
    this.documentActionEvent.emit(ColActionId.snapshot);
  }
}
